import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { HeroBackground } from "@/components/site/HeroBackground";
import { getServiceCategory } from "@/data/site/services";
import { stockImages, type StockImageKey } from "@/data/site/stockImages";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

import ogTaxDebt from "@/assets/og/og-tax-debt-relief.jpg.asset.json";
import ogCollections from "@/assets/og/og-irs-collections-defense.jpg.asset.json";
import ogAudit from "@/assets/og/og-audit-defense.jpg.asset.json";
import ogBusiness from "@/assets/og/og-business-tax.jpg.asset.json";

const categoryImage: Record<string, StockImageKey> = {
  "tax-debt-relief": "piggyBank",
  "irs-collections-defense": "countingCash",
  "audit-defense": "signingForm",
  "business-tax": "accountingDesk",
};

// Bespoke 1200×630 OG cards per category — uniquely branded share previews.
const categoryOgImage: Record<string, string> = {
  "tax-debt-relief": ogTaxDebt.url,
  "irs-collections-defense": ogCollections.url,
  "audit-defense": ogAudit.url,
  "business-tax": ogBusiness.url,
};

const ServiceCategoryPage = () => {
  const { categorySlug = "" } = useParams();
  const category = getServiceCategory(categorySlug);
  if (!category) return <Navigate to="/services" replace />;
  const heroImg = stockImages[categoryImage[category.slug] ?? "taxFormsCalculator"];

  return (
    <SiteLayout>
      <PageSEO
        title={category.meta.title}
        description={category.meta.description}
        path={`/services/${category.slug}`}
        image={categoryOgImage[category.slug] ?? heroImg.src}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Services", href: "/services" },
          { name: category.title, href: `/services/${category.slug}` },
        ]}
      />
      <section className="relative bg-navy-dark text-white overflow-hidden">
        <HeroBackground image={heroImg} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Reveal className="max-w-3xl">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Services", href: "/services" },
                { name: category.title },
              ]} tone="dark"
            />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mt-2 mb-4">
              Service category
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              {category.title}
            </h1>
            <p className="text-lg text-white/85 max-w-2xl leading-relaxed">{category.summary}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-32 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <RevealStagger className="grid md:grid-cols-2 gap-px bg-border" stagger={70}>
            {category.services.map((s) => (
              <RevealItem key={s.slug} className="h-full">
                <Link
                  to={`/services/${category.slug}/${s.slug}`}
                  className="group block bg-white p-8 lg:p-10 hover:bg-secondary transition-colors h-full"
                >
                  <h2 className="font-heading font-bold text-navy text-xl group-hover:text-gold mb-3">
                    {s.title}
                  </h2>
                  <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">
                    {s.tagline}
                  </p>
                  <p className="text-foreground/75 mb-5 leading-relaxed">{s.summary}</p>
                  <span className="text-sm font-semibold text-navy group-hover:text-gold inline-flex items-center gap-1">
                    View {s.title} details <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default ServiceCategoryPage;
